import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom'; // Import useLocation
import { apiService } from '../services/apiService';
import { notificationService } from '../services/notificationService';
import { ChatMessage, Project, Document } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import { saveAs } from 'file-saver';
import html2pdf from 'html2pdf.js';
import { Document as DocxDocument, Packer, Paragraph, TextRun } from 'docx';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

// Add CSS for loading animation and custom scrollbar
const customStyles = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  /* Custom scrollbar styles */
  .custom-scrollbar::-webkit-scrollbar {
    width: 8px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: var(--surface-color);
    border-radius: 4px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: var(--border-color);
    border-radius: 4px;
    transition: background 0.2s ease;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: var(--text-muted);
  }

  .custom-scrollbar::-webkit-scrollbar-corner {
    background: var(--surface-color);
  }
`;

// Inject CSS into document head
if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = customStyles;
  document.head.appendChild(style);
}

const Chat: React.FC = () => {
  const { effectiveTheme } = useTheme();
  const location = useLocation(); // Initialize useLocation
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      type: 'assistant',
      message: "Hello! I'm ready to help you analyze your documents. Ask me anything!",
      timestamp: new Date().toISOString()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [selectedDocuments, setSelectedDocuments] = useState<number[]>([]);
  const [projectDocuments, setProjectDocuments] = useState<Document[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [selectedSource, setSelectedSource] = useState<any>(null);
  const [showSourceModal, setShowSourceModal] = useState(false);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [deepSearchEnabled, setDeepSearchEnabled] = useState(false);
  const [showDeepSearchDialog, setShowDeepSearchDialog] = useState(false);
  const [isProjectsLoading, setIsProjectsLoading] = useState(false);
  const [isDocumentsLoading, setIsDocumentsLoading] = useState(false);

  // Ref for auto-scrolling
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const loadChatHistory = React.useCallback(async () => {
    try {
      const projectId = selectedProject ? parseInt(selectedProject) : undefined;
      console.log('Loading chat history for project:', projectId);

      const historyData = await apiService.getChatHistory(50, projectId);
      console.log('Chat history response:', historyData);

      if (historyData && historyData.messages && historyData.messages.length > 0) {
        const formattedMessages: ChatMessage[] = historyData.messages.map((msg: any) => ({
          id: msg.id.toString(),
          type: msg.type,
          message: msg.content || msg.message, // Handle both content and message fields
          sources: msg.sources,
          timestamp: msg.timestamp,
          isError: msg.is_error
        }));

        console.log('Formatted messages:', formattedMessages);
        setMessages(formattedMessages);
      } else {
        // Set welcome message if no history
        console.log('No chat history found, setting welcome message');
        setMessages([{
          id: 'welcome',
          type: 'assistant',
          message: "Hello! I'm ready to help you analyze your documents. Ask me anything!",
          timestamp: new Date().toISOString()
        }]);
      }
    } catch (error: any) {
      console.error('Error loading chat history:', error);

      // Check if it's a 404 error (no chat history exists yet)
      if (error.response?.status === 404) {
        console.log('No chat session exists yet, setting welcome message');
        setMessages([{
          id: 'welcome',
          type: 'assistant',
          message: "Hello! I'm ready to help you analyze your documents. Ask me anything!",
          timestamp: new Date().toISOString()
        }]);
      } else {
        // For other errors, show error message but still provide welcome
        console.error('Chat history loading failed:', error.message);
        setMessages([{
          id: 'welcome',
          type: 'assistant',
          message: "Hello! I'm ready to help you analyze your documents. Ask me anything!",
          timestamp: new Date().toISOString()
        }]);
      }
    }
  }, [selectedProject]);

  const loadChatData = React.useCallback(async () => {
    setIsProjectsLoading(true);
    try {
      // Load projects for selection
      const projectsData = await apiService.getProjects();
      setProjects(projectsData.projects || []);

      // Load chat suggestions
      const suggestionsData = await apiService.getChatSuggestions();
      setSuggestions(suggestionsData.suggestions || []);

      // Load general chat history if no project selected
      if (!selectedProject) {
        loadChatHistory();
      }
    } catch (error) {
      console.error('Error loading chat data:', error);
    } finally {
      setIsProjectsLoading(false);
    }
  }, [selectedProject, loadChatHistory]);

  useEffect(() => {
    loadChatData();
  }, [loadChatData]);

  useEffect(() => {
    if (selectedProject) {
      loadChatHistory();
    }
  }, [selectedProject, loadChatHistory]);

  const loadProjectDocuments = useCallback(async (projectId: string, initialDocId?: number) => {
    if (!projectId) {
      setProjectDocuments([]);
      setSelectedDocuments([]);
      return;
    }

    setIsDocumentsLoading(true);
    try {
      const data = await apiService.getProjectFiles(parseInt(projectId));
      const completedDocs = data.files?.filter((file: any) => file.status === 'completed') || [];
      setProjectDocuments(completedDocs);

      // If an initialDocId is provided, select only that document
      if (initialDocId) {
        setSelectedDocuments([initialDocId]);
      } else {
        // Otherwise, auto-select all completed documents
        const docIds = completedDocs.map((doc: any) => doc.id);
        setSelectedDocuments(docIds);
      }
    } catch (error) {
      console.error('Error loading project documents:', error);
      notificationService.error('Failed to load project documents');
    } finally {
      setIsDocumentsLoading(false);
    }
  }, []);

  const handleProjectChange = useCallback((projectId: string, initialDocId?: number) => {
    setSelectedProject(projectId);
    loadProjectDocuments(projectId, initialDocId);
    // Don't load README as the endpoint doesn't exist yet
    // loadProjectReadme(projectId);
  }, [loadProjectDocuments]);

  const handleDocumentToggle = (documentId: number) => {
    setSelectedDocuments(prev =>
      prev.includes(documentId)
        ? prev.filter(id => id !== documentId)
        : [...prev, documentId]
    );
  };

  const handleToggleAllDocuments = () => {
    const allSelected = selectedDocuments.length === projectDocuments.length;
    setSelectedDocuments(allSelected ? [] : projectDocuments.map(doc => doc.id));
  };

  const handleDeepSearchToggle = (checked: boolean) => {
    if (checked) {
      setShowDeepSearchDialog(true);
    } else {
      setDeepSearchEnabled(false);
    }
  };

  const handleDeepSearchConfirm = () => {
    setDeepSearchEnabled(true);
    setShowDeepSearchDialog(false);
  };

  const handleDeepSearchCancel = () => {
    setDeepSearchEnabled(false);
    setShowDeepSearchDialog(false);
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    if (selectedDocuments.length === 0) {
      notificationService.warning('Please select at least one document to query');
      return;
    }

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      message: inputMessage,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    // Add loading message
    const loadingMessage: ChatMessage = {
      id: (Date.now() + 1).toString(),
      type: 'assistant',
      message: 'Thinking...',
      timestamp: new Date().toISOString(),
      isLoading: true
    };
    setMessages(prev => [...prev, loadingMessage]);

    try {
      const response = await apiService.sendChatQuery({
        message: inputMessage,
        document_ids: selectedDocuments,
        context_limit: 8,
        project_id: selectedProject ? parseInt(selectedProject) : undefined,
        enable_deep_search: deepSearchEnabled
      });

      // Remove loading message and add response
      setMessages(prev => {
        const filtered = prev.filter(msg => !msg.isLoading);
        return [...filtered, {
          id: (Date.now() + 2).toString(),
          type: 'assistant',
          message: response.response,
          sources: response.sources,
          timestamp: new Date().toISOString()
        }];
      });

    } catch (error: any) {
      console.error('Chat error:', error);

      // Remove loading message and add error
      setMessages(prev => {
        const filtered = prev.filter(msg => !msg.isLoading);
        return [...filtered, {
          id: (Date.now() + 2).toString(),
          type: 'assistant',
          message: `Error: ${error.response?.data?.detail || error.message || 'Failed to process your request'}`,
          timestamp: new Date().toISOString(),
          isError: true
        }];
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const applySuggestion = (suggestion: string, promptType?: string) => {
    setInputMessage(suggestion);
    // If a prompt type is provided, send the query immediately
    if (promptType) {
      handleSendMessageWithPromptType(suggestion, promptType);
    }
  };

  const handleSendMessageWithPromptType = async (message: string, promptType: string) => {
    if (selectedDocuments.length === 0) {
      notificationService.warning('Please select at least one document to query');
      return;
    }

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      type: 'user',
      message: message,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    // Add loading message
    const loadingMessage: ChatMessage = {
      id: (Date.now() + 1).toString(),
      type: 'assistant',
      message: 'Thinking...',
      timestamp: new Date().toISOString(),
      isLoading: true
    };
    setMessages(prev => [...prev, loadingMessage]);

    try {
      const response = await apiService.sendChatQuery({
        message: message,
        document_ids: selectedDocuments,
        context_limit: 8,
        project_id: selectedProject ? parseInt(selectedProject) : undefined,
        enable_deep_search: deepSearchEnabled,
        prompt_type: promptType
      });

      // Remove loading message and add response
      setMessages(prev => {
        const filtered = prev.filter(msg => !msg.isLoading);
        return [...filtered, {
          id: (Date.now() + 2).toString(),
          type: 'assistant',
          message: response.response,
          sources: response.sources,
          timestamp: new Date().toISOString()
        }];
      });

    } catch (error: any) {
      console.error('Chat error:', error);

      // Remove loading message and add error
      setMessages(prev => {
        const filtered = prev.filter(msg => !msg.isLoading);
        return [...filtered, {
          id: (Date.now() + 2).toString(),
          type: 'assistant',
          message: `Error: ${error.response?.data?.detail || error.message || 'Failed to process your request'}`,
          timestamp: new Date().toISOString(),
          isError: true
        }];
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Process message content to handle inline citations BEFORE markdown processing
  const processMessageWithCitations = (message: string, sources?: any[]) => {
    let processed = message;

    // CRITICAL: Clear previous citation storage to prevent cross-contamination
    (window as any).citationSources = {};
    console.log('🧹 CLEARED citation storage for new message processing');

    // Remove HTML attributes that might be showing as text
    processed = processed.replace(/onmouseover="[^"]*"/gi, '');
    processed = processed.replace(/onmouseout="[^"]*"/gi, '');
    processed = processed.replace(/style="[^"]*"/gi, '');
    processed = processed.replace(/class="[^"]*"/gi, '');
    processed = processed.replace(/id="[^"]*"/gi, '');

    // Remove any remaining HTML-like attributes
    processed = processed.replace(/\s*(onmouseover|onmouseout|style|class|id)="[^"]*"/gi, '');

    // Remove stray HTML content
    processed = processed.replace(/\s*onmouseover[^>]*>/gi, '');
    processed = processed.replace(/\s*onmouseout[^>]*>/gi, '');

    // Clean up malformed HTML tags
    processed = processed.replace(/<[^>]*onmouseover[^>]*>/gi, '');
    processed = processed.replace(/<[^>]*onmouseout[^>]*>/gi, '');

    // Process URL-style citations first - format: ([domain.com](https://full-url))
    const urlCitationPattern = /\(\[([^\]]+)\]\((https?:\/\/[^)]+)\)\)/g;
    processed = processed.replace(urlCitationPattern, (match, linkText, url) => {
      console.log(`🔗 Found URL citation: "${linkText}" -> ${url}`);

      // Create a clickable link for URL citations
      const sourceId = `URL_CITATION_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      // Store the URL citation data
      (window as any).citationSources = (window as any).citationSources || {};
      (window as any).citationSources[sourceId] = {
        type: 'url',
        text: linkText,
        url: url,
        isExternal: true
      };

      return `<span data-citation="${sourceId}">([${linkText}](${url}))</span>`;
    });

    // Process document citations BEFORE markdown rendering
    if (sources && sources.length > 0) {
      // Pattern to match citations like [Information, Page 3] or [Document Name, Page 5]
      const citationPattern = /\[([^\]]+(?:,\s*Page\s+\d+)?)\]/g;

      processed = processed.replace(citationPattern, (match, citationText) => {
        // Extract citation parts
        const pageMatch = citationText.match(/Page\s+(\d+)/i);
        const citationPage = pageMatch ? parseInt(pageMatch[1]) : null;

        // Get document name from citation (everything before ", Page")
        const citationDocName = citationText.split(',')[0].trim();

        // Find the document from project files API that matches the citation filename
        const matchedDocument = projectDocuments.find(doc => {
          const docName = doc.display_name || doc.original_filename;
          const docNameWithoutExt = docName.split('.')[0];

          // Try exact match first
          if (docNameWithoutExt.toLowerCase() === citationDocName.toLowerCase()) {
            return true;
          }

          // Try partial matches for common variations
          const citationLower = citationDocName.toLowerCase();
          const docLower = docNameWithoutExt.toLowerCase();

          // Handle common filename variations
          if (citationLower.includes('1st-app-h-ptl-group-cim') && docLower.includes('1st-app-h-ptl-group-cim')) {
            return true;
          }
          if (citationLower.includes('cim-02-american-casino') && docLower.includes('cim-02-american-casino')) {
            return true;
          }
          if (citationLower.includes('lender-information-memorandum') && docLower.includes('lender-information-memorandum')) {
            return true;
          }

          return false;
        });

        let matchedSource: any = null;

        if (matchedDocument) {
          console.log(`📄 Citation "${citationText}" matched to document:`, {
            id: matchedDocument.id,
            name: matchedDocument.display_name || matchedDocument.original_filename,
            citationDocName
          });

          // Find source with matching document_id from the files API data
          console.log(`🔍 Looking for sources with document_id ${matchedDocument.id}`);
          console.log('📊 DETAILED SOURCES ANALYSIS:');
          console.table(sources.map((s, index) => ({
            index,
            document_id: s.metadata?.document_id,
            page: s.metadata?.page,
            filename: s.metadata?.filename || 'N/A',
            text_preview: s.text?.substring(0, 100) + '...' || 'N/A',
            has_metadata: !!s.metadata,
            metadata_keys: s.metadata ? Object.keys(s.metadata).join(', ') : 'N/A'
          })));

          // Also log the raw sources data for complete visibility
          console.log('🔍 RAW SOURCES DATA:', JSON.stringify(sources, null, 2));

          matchedSource = sources.find(source =>
            source.metadata?.document_id === matchedDocument.id
          );

          // If we have a page number, try to find exact page match within the same document
          if (citationPage && matchedDocument.id) {
            const exactPageMatch = sources.find(source =>
              source.metadata?.document_id === matchedDocument.id &&
              source.metadata?.page === citationPage
            );
            if (exactPageMatch) {
              console.log(`📄 Found exact page match for page ${citationPage} in document ${matchedDocument.id}`);
              matchedSource = exactPageMatch;
            } else {
              console.log(`⚠️ No exact page match found for page ${citationPage} in document ${matchedDocument.id}`);
            }
          }

          if (matchedSource) {
            console.log(`✅ Successfully matched citation to source:`, {
              documentId: matchedDocument.id,
              documentName: matchedDocument.display_name || matchedDocument.original_filename,
              sourcePage: matchedSource.metadata?.page,
              citationPage
            });
          } else {
            console.warn(`⚠️ No source found for document ID ${matchedDocument.id}`);
          }
        } else {
          console.warn(`⚠️ No document found for citation "${citationDocName}"`);
          console.log('📋 AVAILABLE PROJECT DOCUMENTS:');
          console.table(projectDocuments.map(doc => ({
            id: doc.id,
            display_name: doc.display_name || 'N/A',
            original_filename: doc.original_filename || 'N/A',
            name_without_ext: (doc.display_name || doc.original_filename || '').split('.')[0],
            citation_match_attempt: citationDocName.toLowerCase()
          })));
        }

        // Fallback: try page-only matching if no document match found
        if (!matchedSource && citationPage) {
          console.log(`🔄 Trying page-only fallback for page ${citationPage}`);
          // Find sources that match the exact page number
          const pageMatches = sources.filter(source => source.metadata?.page === citationPage);

          if (pageMatches.length > 0) {
            // If we have multiple page matches, try to find one that also matches document name
            const bestPageMatch = pageMatches.find(source => {
              const docId = source.metadata?.document_id;
              const document = projectDocuments.find(doc => doc.id === docId);
              if (document) {
                const docName = document.display_name || document.original_filename;
                const docNameWithoutExt = docName.split('.')[0];
                return docNameWithoutExt.toLowerCase().includes(citationDocName.toLowerCase()) ||
                  citationDocName.toLowerCase().includes(docNameWithoutExt.toLowerCase());
              }
              return false;
            });

            matchedSource = bestPageMatch || pageMatches[0];
            console.log(`✅ Found page-only match for page ${citationPage} in document ${matchedSource.metadata?.document_id}`);
          }
        }

        // Enhanced fallback: try to find any source from the same document type or with similar content
        if (!matchedSource && sources.length > 0) {
          console.log(`🔄 Trying enhanced fallback for citation "${citationText}"`);

          // Try to find a source with similar document name in the text content
          const docNameParts = citationDocName.toLowerCase().split(/[-\s_]+/);
          matchedSource = sources.find(source => {
            const docId = source.metadata?.document_id;
            const document = projectDocuments.find(doc => doc.id === docId);

            if (document) {
              const docName = (document.display_name || document.original_filename).toLowerCase();
              // Check if any part of the citation name matches the document name
              return docNameParts.some((part: string) =>
                part.length > 2 && docName.includes(part)
              );
            }

            // Also check text content as secondary match
            const sourceText = source.text?.toLowerCase() || '';
            return docNameParts.some((part: string) => part.length > 3 && sourceText.includes(part));
          });

          if (matchedSource) {
            console.log(`✅ Found content-based match for "${citationDocName}" in document ${matchedSource.metadata?.document_id}`);
          } else {
            // Last resort: DON'T use first source as fallback - this was causing the wrong matches
            console.log(`⚠️ No suitable match found for "${citationText}" - citation will not be clickable`);
            matchedSource = null;
          }
        }

        // Create a unique placeholder that won't be processed by markdown
        if (matchedSource) {
          const sourceId = `CITATION_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

          // CRITICAL DEBUG: Log what we're storing
          console.log(`🔒 STORING CITATION DATA for "${citationText}":`, {
            sourceId,
            storedSource: {
              document_id: matchedSource.metadata?.document_id,
              page: matchedSource.metadata?.page,
              text_preview: matchedSource.text?.substring(0, 100) + '...'
            },
            citationText
          });

          // Store the source data globally for later retrieval
          (window as any).citationSources = (window as any).citationSources || {};
          (window as any).citationSources[sourceId] = {
            source: matchedSource,
            text: citationText
          };

          // Return a placeholder that markdown won't process
          return `<span data-citation="${sourceId}">[${citationText}]</span>`;
        } else {
          console.log(`⚠️ No source found for citation "${citationText}" - will not be clickable`);
        }

        return match; // Return original if no source found
      });
    }

    return processed;
  };

  const formatSourceContent = (content: string): string => {
    if (!content) return 'No content available';

    // Clean up any HTML/CSS artifacts that might be in the content
    let cleanContent = content;

    // Remove CSS-like content (anything that looks like CSS properties)
    cleanContent = cleanContent.replace(/^\s*[a-z-]+\s*:\s*[^;]+;?\s*$/gm, '');
    cleanContent = cleanContent.replace(/^\s*\d+px\s+\d+[^;]*;?\s*$/gm, '');
    cleanContent = cleanContent.replace(/^\s*width\s*100%;\s*$/gm, '');
    cleanContent = cleanContent.replace(/^\s*border-[a-z]+\s*[^;]+;\s*$/gm, '');
    cleanContent = cleanContent.replace(/^\s*background\s*[^;]+;\s*$/gm, '');
    cleanContent = cleanContent.replace(/^\s*color\s*#[a-fA-F0-9]+;\s*$/gm, '');
    cleanContent = cleanContent.replace(/^\s*font-[a-z]+\s*[^;]+;\s*$/gm, '');
    cleanContent = cleanContent.replace(/^\s*padding\s*[^;]+;\s*$/gm, '');
    cleanContent = cleanContent.replace(/^\s*margin\s*[^;]+;\s*$/gm, '');
    cleanContent = cleanContent.replace(/^\s*box-shadow\s*[^;]+;\s*$/gm, '');
    cleanContent = cleanContent.replace(/^\s*border-radius\s*[^;]+;\s*$/gm, '');
    cleanContent = cleanContent.replace(/^\s*overflow\s*[^;]+;\s*$/gm, '');

    // Remove HTML attributes
    cleanContent = cleanContent.replace(/onmouseover="[^"]*"/gi, '');
    cleanContent = cleanContent.replace(/onmouseout="[^"]*"/gi, '');
    cleanContent = cleanContent.replace(/style="[^"]*"/gi, '');
    cleanContent = cleanContent.replace(/class="[^"]*"/gi, '');
    cleanContent = cleanContent.replace(/id="[^"]*"/gi, '');

    // Remove empty lines and trim
    cleanContent = cleanContent.replace(/^\s*$/gm, '').replace(/\n\n+/g, '\n\n').trim();

    // If the content is still mostly CSS/HTML artifacts, return a message
    if (cleanContent.length < 50 || /^[\s\n]*$/.test(cleanContent)) {
      return 'Source content is not available in readable format.';
    }

    // Simple formatting for readability
    cleanContent = cleanContent
      // Convert line breaks to HTML breaks for better display
      .replace(/\n/g, '<br>')
      // Bold any text that looks like headers or labels
      .replace(/^([A-Z][A-Z\s]+):(.*)$/gm, '<strong>$1:</strong>$2')
      // Format financial data
      .replace(/\$([0-9,]+(?:\.[0-9]+)?)\s*(billion|million|thousand)?/gi, '<strong>$$$1 $2</strong>')
      // Format percentages
      .replace(/([0-9]+(?:\.[0-9]+)?%)/g, '<strong>$1</strong>');

    return cleanContent;
  };

  const getFileIcon = (fileType: string) => {
    switch (fileType?.toLowerCase()) {
      case 'pdf': return 'pdf';
      case 'docx': return 'word';
      case 'pptx': return 'ppt';
      case 'xlsx': return 'excel';
      default: return 'text';
    }
  };

  const handleDownloadDocx = async (content: string) => {
    const doc = new DocxDocument({
      sections: [{
        properties: {},
        children: [
          new Paragraph({
            children: [
              new TextRun(content)
            ],
          }),
        ],
      }],
    });

    const blob = await Packer.toBlob(doc);
    saveAs(blob, `ancore-response-${Date.now()}.docx`);
    notificationService.success('Exported as DOCX');
  };


  // Auto-scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Check if user is near bottom of chat
  const checkScrollPosition = useCallback(() => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
      const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
      setShowScrollButton(!isNearBottom);
    }
  }, [messagesContainerRef, setShowScrollButton]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Add scroll event listener
  useEffect(() => {
    const handleScroll = () => {
      checkScrollPosition();
    };

    const container = messagesContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [checkScrollPosition, messagesContainerRef]);

  // Global function to handle source clicks
  React.useEffect(() => {
    (window as any).showSourceDetails = (source: any, citationText?: string, sources?: any[]) => {
      // Log debug info only for the clicked citation
      if (citationText && sources) {
        console.log(`🎯 CLICKED CITATION DEBUG: "${citationText}"`);
        console.log('📊 SOURCES ANALYSIS FOR THIS CITATION:');
        console.table(sources.map((s, index) => ({
          index,
          document_id: s.metadata?.document_id,
          page: s.metadata?.page,
          filename: s.metadata?.filename || 'N/A',
          text_preview: s.text?.substring(0, 100) + '...' || 'N/A',
          has_metadata: !!s.metadata,
          metadata_keys: s.metadata ? Object.keys(s.metadata).join(', ') : 'N/A'
        })));

        console.log('🔍 RAW SOURCES DATA FOR THIS CITATION:', JSON.stringify(sources, null, 2));

        // Show which source was actually selected
        console.log('✅ SELECTED SOURCE FOR THIS CITATION:', {
          document_id: source.metadata?.document_id,
          page: source.metadata?.page,
          filename: source.metadata?.filename,
          text_preview: source.text?.substring(0, 100) + '...'
        });

        // CRITICAL DEBUG: Check what's actually being passed to modal
        console.log('🚨 CRITICAL: Source being passed to modal:', JSON.stringify(source, null, 2));
        console.log('🚨 CRITICAL: Expected citation text:', citationText);

        // Verify the source matches the citation
        const expectedDocName = citationText.split(',')[0].trim();
        const actualDocId = source.metadata?.document_id;
        const actualPage = source.metadata?.page;

        console.log('🔍 VERIFICATION CHECK:');
        console.log('  Expected doc name:', expectedDocName);
        console.log('  Actual doc ID:', actualDocId);
        console.log('  Actual page:', actualPage);

        // Find the expected document
        const expectedDoc = projectDocuments.find(doc => {
          const docName = doc.display_name || doc.original_filename;
          const docNameWithoutExt = docName.split('.')[0];
          return docNameWithoutExt.toLowerCase() === expectedDocName.toLowerCase();
        });

        if (expectedDoc) {
          console.log('  Expected doc ID:', expectedDoc.id);
          console.log('  ✅ Match status:', actualDocId === expectedDoc.id ? 'CORRECT' : '❌ MISMATCH!');
        } else {
          console.log('  ⚠️ Could not find expected document');
        }
      }

      setSelectedSource(source);
      setShowSourceModal(true);
    };

    return () => {
      delete (window as any).showSourceDetails;
    };
  }, [projectDocuments]);

  // Effect to handle query parameters for initial project and document selection
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const projectIdFromUrl = queryParams.get('project_id');
    const docIdFromUrl = queryParams.get('doc_id');

    if (projectIdFromUrl) {
      setSelectedProject(projectIdFromUrl);
      // Load project documents and then select the specific document if docIdFromUrl exists
      loadProjectDocuments(projectIdFromUrl, docIdFromUrl ? parseInt(docIdFromUrl) : undefined);
    }
  }, [location.search, loadProjectDocuments]);

  return (
    <div>
      <div style={{
        display: 'flex',
        gap: '1.5rem',
        padding: '1.5rem',
        height: 'calc(100vh - 60px)', // Use full viewport height minus navbar/padding
        boxSizing: 'border-box'
      }}>
        <div style={{
          flex: '1',
          minWidth: 0, // Allow flex item to shrink
          background: 'var(--bg-color)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-color)',
          boxShadow: 'var(--shadow-sm)',
          display: 'flex',
          flexDirection: 'column',
          height: '100%'
        }}>
            <div style={{
              padding: '1.5rem 1.5rem 1rem 1.5rem',
              borderBottom: '1px solid var(--border-color)',
              flexShrink: 0
            }}>
              <h6 style={{ margin: 0, color: 'var(--text-primary)', fontWeight: 600 }}>AI Assistant</h6>
            </div>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0
            }}>
              {/* Enhanced Chat Messages */}
              <div style={{ position: 'relative', flex: 1, minHeight: 0 }}>
                <div
                  ref={messagesContainerRef}
                  style={{
                    height: '100%',
                    overflowY: 'auto',
                    padding: '1.5rem',
                    background: 'var(--surface-color)',
                    /* Custom scrollbar styling */
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'var(--border-color) var(--surface-color)'
                  }}
                  className="custom-scrollbar"
                >
                  {messages.map((message) => (
                    <div key={message.id} style={{ marginBottom: '1rem', display: 'flex', justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start' }}>
                      <div
                        style={{
                          maxWidth: '80%',
                          padding: '1rem',
                          borderRadius: 'var(--radius-lg)',
                          background: message.type === 'user'
                            ? 'var(--text-primary)'
                            : message.isError
                              ? (effectiveTheme === 'dark' ? 'rgba(239, 68, 68, 0.2)' : '#fee2e2')
                              : 'var(--bg-color)',
                          color: message.type === 'user' ? 'var(--bg-color)' : 'var(--text-primary)',
                          border: message.type === 'assistant' ? '1px solid var(--border-color)' : 'none',
                          boxShadow: message.type === 'assistant' ? 'var(--shadow-sm)' : 'none'
                        }}
                      >
                        {message.isLoading ? (
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <div style={{
                              width: '16px',
                              height: '16px',
                              border: '2px solid var(--border-color)',
                              borderTop: '2px solid var(--primary-color)',
                              borderRadius: '50%',
                              animation: 'spin 1s linear infinite',
                              marginRight: '0.5rem'
                            }}></div>
                            <span>Thinking...</span>
                          </div>
                        ) : (
                          <>
                            <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <strong>
                                <i className={`bi ${message.type === 'user' ? 'bi-person-circle' : 'bi-robot'}`} style={{ marginRight: '0.5rem' }}></i>
                                {message.type === 'user' ? 'You' : 'AnCore'}
                              </strong>
                              {message.type === 'assistant' && !message.isLoading && !message.isError && (
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                  <button
                                    onClick={() => {
                                      navigator.clipboard.writeText(message.message);
                                      notificationService.success('Message copied to clipboard');
                                    }}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      color: 'var(--text-secondary)',
                                      padding: '0.25rem',
                                      fontSize: '0.875rem',
                                      transition: 'color 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.color = 'var(--primary-color)'}
                                    onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}
                                    title="Copy message"
                                  >
                                    <i className="bi bi-clipboard"></i>
                                  </button>
                                  <div style={{ position: 'relative' }}>
                                    <button
                                      onClick={(e) => {
                                        const btn = e.currentTarget;
                                        const menu = btn.nextElementSibling as HTMLElement;
                                        if (menu) {
                                          menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
                                        }
                                      }}
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        cursor: 'pointer',
                                        color: 'var(--text-secondary)',
                                        padding: '0.25rem',
                                        fontSize: '0.875rem',
                                        transition: 'color 0.2s'
                                      }}
                                      onMouseEnter={(e) => e.currentTarget.style.color = 'var(--primary-color)'}
                                      onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}
                                      title="Export message"
                                    >
                                      <i className="bi bi-download"></i>
                                    </button>
                                    <div
                                      style={{
                                        display: 'none',
                                        position: 'absolute',
                                        right: 0,
                                        top: '100%',
                                        background: 'var(--bg-color)',
                                        border: '1px solid var(--border-color)',
                                        borderRadius: 'var(--radius-sm)',
                                        boxShadow: 'var(--shadow-lg)',
                                        zIndex: 1000,
                                        minWidth: '120px',
                                        marginTop: '0.25rem'
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <button
                                        onClick={() => {
                                          const blob = new Blob([message.message], { type: 'text/plain' });
                                          const url = URL.createObjectURL(blob);
                                          const a = document.createElement('a');
                                          a.href = url;
                                          a.download = `message-${Date.now()}.txt`;
                                          a.click();
                                          URL.revokeObjectURL(url);
                                          notificationService.success('Exported as TXT');
                                        }}
                                        style={{
                                          width: '100%',
                                          padding: '0.5rem 1rem',
                                          background: 'none',
                                          border: 'none',
                                          textAlign: 'left',
                                          cursor: 'pointer',
                                          color: 'var(--text-primary)',
                                          fontSize: '0.875rem',
                                          transition: 'background 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-color)'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                                      >
                                        <i className="bi bi-file-text" style={{ marginRight: '0.5rem' }}></i>TXT
                                      </button>
                                      <button
                                        onClick={() => {
                                          const blob = new Blob([message.message], { type: 'text/markdown' });
                                          const url = URL.createObjectURL(blob);
                                          const a = document.createElement('a');
                                          a.href = url;
                                          a.download = `message-${Date.now()}.md`;
                                          a.click();
                                          URL.revokeObjectURL(url);
                                          notificationService.success('Exported as Markdown');
                                        }}
                                        style={{
                                          width: '100%',
                                          padding: '0.5rem 1rem',
                                          background: 'none',
                                          border: 'none',
                                          textAlign: 'left',
                                          cursor: 'pointer',
                                          color: 'var(--text-primary)',
                                          fontSize: '0.875rem',
                                          transition: 'background 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-color)'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                                      >
                                        <i className="bi bi-markdown" style={{ marginRight: '0.5rem' }}></i>MD
                                      </button>
                                      <button
                                        onClick={() => {
                                          const contentHtml = `
                                          <!DOCTYPE html>
                                          <html>
                                          <head>
                                            <meta charset="utf-8">
                                            <title>AnCore AI Response</title>
                                            <style>
                                              body {
                                                font-family: 'Segoe UI', Arial, sans-serif;
                                                line-height: 1.6;
                                                padding: 20px;
                                                max-width: 800px;
                                                margin: 0 auto;
                                                color: #333; /* Ensure text is black */
                                                background-color: #fff; /* Ensure background is white */
                                              }
                                              h1 {
                                                color: #2c3e50;
                                                border-bottom: 3px solid #3498db;
                                                padding-bottom: 10px;
                                                margin-top: 20px;
                                              }
                                              h2 {
                                                color: #34495e;
                                                border-bottom: 2px solid #95a5a6;
                                                padding-bottom: 8px;
                                                margin-top: 18px;
                                              }
                                              h3 {
                                                color: #555;
                                                margin-top: 16px;
                                              }
                                              h4 {
                                                color: #666;
                                                margin-top: 14px;
                                              }
                                              p {
                                                margin: 10px 0;
                                                text-align: justify;
                                              }
                                              ul, ol {
                                                margin: 10px 0;
                                                padding-left: 30px;
                                              }
                                              li {
                                                margin: 5px 0;
                                              }
                                              strong {
                                                color: #2c3e50;
                                                font-weight: 600;
                                              }
                                              code {
                                                background: #f4f4f4;
                                                padding: 2px 6px;
                                                border-radius: 3px;
                                                font-family: 'Courier New', monospace;
                                              }
                                              pre {
                                                background: #f8f9fa;
                                                padding: 15px;
                                                border-radius: 5px;
                                                overflow-x: auto;
                                                border-left: 4px solid #3498db;
                                              }
                                              hr {
                                                border: none;
                                                border-top: 1px solid #ddd;
                                                margin: 20px 0;
                                              }
                                            </style>
                                          </head>
                                          <body>
                                            <h1>AnCore AI Response</h1>
                                            <hr>
                                            ${message.message}
                                          </body>
                                          </html>
                                        `;

                                          const element = document.createElement('div');
                                          element.innerHTML = contentHtml;

                                          html2pdf().from(element).set({
                                            margin: [10, 10, 10, 10],
                                            filename: `ancore-response-${Date.now()}.pdf`,
                                            image: { type: 'jpeg', quality: 0.98 },
                                            html2canvas: { scale: 2, logging: true, dpi: 192, letterRendering: true },
                                            jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
                                          }).save();
                                          notificationService.success('Exported as PDF');
                                        }}
                                        style={{
                                          width: '100%',
                                          padding: '0.5rem 1rem',
                                          background: 'none',
                                          border: 'none',
                                          textAlign: 'left',
                                          cursor: 'pointer',
                                          color: 'var(--text-primary)',
                                          fontSize: '0.875rem',
                                          transition: 'background 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-color)'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                                      >
                                        <i className="bi bi-file-pdf" style={{ marginRight: '0.5rem' }}></i>PDF
                                      </button>
                                      <button
                                        onClick={() => handleDownloadDocx(message.message)}
                                        style={{
                                          width: '100%',
                                          padding: '0.5rem 1rem',
                                          background: 'none',
                                          border: 'none',
                                          textAlign: 'left',
                                          cursor: 'pointer',
                                          color: 'var(--text-primary)',
                                          fontSize: '0.875rem',
                                          transition: 'background 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-color)'}
                                        onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                                      >
                                        <i className="bi bi-file-word" style={{ marginRight: '0.5rem' }}></i>DOCX
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                            <div>
                              {message.type === 'assistant' && !message.isError ? (
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: (() => {
                                      // Process the message with citations first
                                      const processedMessage = processMessageWithCitations(message.message, message.sources);

                                      // Convert markdown to HTML using a simple approach
                                      let htmlContent = processedMessage;

                                      // Process tables first (before other replacements)
                                      htmlContent = htmlContent.replace(/(\|[^\n]*\|[\n\r]*)+/g, (tableMatch) => {
                                        const lines = tableMatch.trim().split(/[\n\r]+/);
                                        if (lines.length < 2) return tableMatch;

                                        let tableHtml = '<div style="margin: 1rem 0; overflow-x: auto;"><table style="width: 100%; border-collapse: collapse; background: var(--bg-color); border-radius: 8px; overflow: hidden; box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.1); border: 1px solid var(--border-color);">';

                                        lines.forEach((line, index) => {
                                          const cells = line.split('|').map(cell => cell.trim()).filter(cell => cell !== '');

                                          // Skip separator lines (lines with only dashes and pipes)
                                          if (line.match(/^[\s|\-+=]*$/)) return;

                                          if (cells.length > 0) {
                                            const isHeader = index === 0 || (index === 1 && lines[0].match(/^[\s|\-+=]*$/));
                                            const tag = isHeader ? 'th' : 'td';
                                            const style = isHeader
                                              ? 'padding: 12px; border-bottom: 2px solid var(--border-color); font-weight: 600; color: var(--text-primary); background: var(--surface-color); text-align: left;'
                                              : 'padding: 12px; border-bottom: 1px solid var(--border-color); color: var(--text-primary);';

                                            tableHtml += '<tr>';
                                            cells.forEach(cell => {
                                              tableHtml += `<${tag} style="${style}">${cell}</${tag}>`;
                                            });
                                            tableHtml += '</tr>';
                                          }
                                        });

                                        tableHtml += '</table></div>';
                                        return tableHtml;
                                      });

                                      // Then process other markdown elements
                                      htmlContent = htmlContent
                                        // Math expressions (LaTeX-style) - process before other replacements
                                        .replace(/\\\[([\s\S]*?)\\\]/g, '<div style="margin: 1rem 0; padding: 1rem; background: var(--surface-color); border-radius: 8px; border: 1px solid var(--border-color); font-family: \'Times New Roman\', serif; text-align: center; font-size: 1.1rem;">$1</div>')
                                        .replace(/\\text\{([^}]+)\}/g, '$1')
                                        .replace(/\\frac\{([^}]+)\}\{([^}]+)\}/g, '<span style="display: inline-block; text-align: center; vertical-align: middle;"><span style="display: block; border-bottom: 1px solid currentColor; padding-bottom: 2px;">$1</span><span style="display: block; padding-top: 2px;">$2</span></span>')
                                        .replace(/\\left\(/g, '(').replace(/\\right\)/g, ')')
                                        .replace(/\\\\/g, '<br>')
                                        // Headers (process from most specific to least specific) - minimal spacing
                                        .replace(/(^|\n)#### (.*?)(?=\n|$)/gim, '$1<h4 style="font-size: 1rem; font-weight: 600; margin: 0.3rem 0 0.2rem 0; color: var(--text-primary);">$2</h4>')
                                        .replace(/(^|\n)### (.*?)(?=\n|$)/gim, '$1<h3 style="font-size: 1.1rem; font-weight: 600; margin: 0.3rem 0 0.2rem 0; color: var(--text-primary);">$2</h3>')
                                        .replace(/(^|\n)## (.*?)(?=\n|$)/gim, '$1<h2 style="font-size: 1.25rem; font-weight: 600; margin: 0.4rem 0 0.2rem 0; color: var(--text-primary); border-bottom: 1px solid var(--border-color); padding-bottom: 0.1rem;">$2</h2>')
                                        .replace(/(^|\n)# (.*?)(?=\n|$)/gim, '$1<h1 style="font-size: 1.5rem; font-weight: 600; margin: 0.5rem 0 0.2rem 0; color: var(--text-primary); border-bottom: 2px solid var(--primary-color); padding-bottom: 0.2rem;">$2</h1>')
                                        // Horizontal rules - handle various formats
                                        .replace(/^---+\s*$/gim, '<hr style="border: none; border-top: 1px solid var(--border-color); margin: 1rem 0;">')
                                        .replace(/^\*\*\*+\s*$/gim, '<hr style="border: none; border-top: 1px solid var(--border-color); margin: 1rem 0;">')
                                        .replace(/^___+\s*$/gim, '<hr style="border: none; border-top: 1px solid var(--border-color); margin: 1rem 0;">')
                                        // Bold and italic
                                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                        .replace(/\*(.*?)\*/g, '<em>$1</em>')
                                        // Lists
                                        .replace(/^\* (.*$)/gim, '<li style="margin: 0.25rem 0;">$1</li>')
                                        .replace(/^- (.*$)/gim, '<li style="margin: 0.25rem 0;">$1</li>')
                                        // Line breaks (but preserve table HTML and math expressions) - minimal spacing
                                        .replace(/\n\n(?![^<]*<\/(table|div)>)/g, '</p><p style="margin: 0.1rem 0; line-height: 1.4;">')
                                        .replace(/\n(?![^<]*<\/(table|div)>)/g, '<br>');

                                      // Wrap in paragraph if not already wrapped - minimal spacing
                                      if (!htmlContent.includes('<p>') && !htmlContent.includes('<h1>') && !htmlContent.includes('<h2>') && !htmlContent.includes('<h3>')) {
                                        htmlContent = `<p style="margin: 0.1rem 0; line-height: 1.4;">${htmlContent}</p>`;
                                      }

                                      // Wrap lists in ul tags - reduced spacing
                                      htmlContent = htmlContent.replace(/(<li[^>]*>.*?<\/li>)/g, (match) => {
                                        return `<ul style="margin: 0.2rem 0; padding-left: 1.5rem;">${match}</ul>`;
                                      });

                                      // Now process the citation placeholders
                                      htmlContent = htmlContent.replace(/<span data-citation="([^"]+)">([^<]+)<\/span>/g, (match, sourceId, citationText) => {
                                        const citationData = (window as any).citationSources?.[sourceId];
                                        if (citationData) {
                                          // Handle URL citations differently from document citations
                                          if (citationData.type === 'url') {
                                            // For URL citations, create an external link
                                            return `<a 
                           href="${citationData.url}" 
                           target="_blank" 
                           rel="noopener noreferrer"
                           style="
                             color: #3b82f6;
                             text-decoration: none;
                             font-weight: 500;
                             padding: 2px 4px;
                             border-radius: 4px;
                             transition: all 0.2s ease;
                             background-color: rgba(59, 130, 246, 0.1);
                             border: 1px solid rgba(59, 130, 246, 0.2);
                           "
                           onmouseenter="
                             this.style.backgroundColor = 'rgba(59, 130, 246, 0.2)';
                             this.style.borderColor = 'rgba(59, 130, 246, 0.4)';
                             this.style.color = '#1d4ed8';
                           "
                           onmouseleave="
                             this.style.backgroundColor = 'rgba(59, 130, 246, 0.1)';
                             this.style.borderColor = 'rgba(59, 130, 246, 0.2)';
                             this.style.color = '#3b82f6';
                           "
                           title="External link: ${citationData.url}"
                         >🔗 ${citationData.text}</a>`;
                                          } else {
                                            // For document citations, use the existing modal approach
                                            const clickHandlerId = `citation_${sourceId}`;
                                            (window as any)[clickHandlerId] = () => {
                                              (window as any).showSourceDetails && (window as any).showSourceDetails(
                                                citationData.source,
                                                citationData.text,
                                                message.sources
                                              );
                                            };

                                            const clickHandler = `window.${clickHandlerId} && window.${clickHandlerId}()`;

                                            const mouseEnter = `
                           this.style.backgroundColor = 'rgba(59, 130, 246, 0.2)';
                           this.style.color = '#1d4ed8';
                         `;

                                            const mouseLeave = `
                           this.style.backgroundColor = 'rgba(59, 130, 246, 0.1)';
                           this.style.borderColor = 'rgba(59, 130, 246, 0.2)';
                           this.style.color = '#3b82f6';
                         `;

                                            return `<span 
                           onclick="${clickHandler}"
                           onmouseenter="${mouseEnter}"
                           onmouseleave="${mouseLeave}"
                           style="
                             color: #3b82f6;
                             cursor: pointer;
                             text-decoration: underline;
                             font-weight: 500;
                             padding: 1px 2px;
                             border-radius: 3px;
                             transition: all 0.2s ease;
                             background-color: rgba(59, 130, 246, 0.1);
                           "
                         >${citationText}</span>`;
                                          }
                                        }
                                        return citationText;
                                      });

                                      return htmlContent;
                                    })()
                                  }}
                                />
                              ) : (
                                <div style={{ whiteSpace: 'pre-wrap' }}>{message.message}</div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                  {/* Invisible div to scroll to */}
                  <div ref={messagesEndRef} />
                </div>

                {/* Scroll to Bottom Button */}
                {showScrollButton && (
                  <button
                    onClick={scrollToBottom}
                    style={{
                      position: 'absolute',
                      bottom: '20px',
                      right: '20px',
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      background: 'var(--primary-color)',
                      color: 'white',
                      border: 'none',
                      cursor: 'pointer',
                      boxShadow: 'var(--shadow-lg)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.2rem',
                      transition: 'all var(--transition-normal)',
                      zIndex: 10
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'var(--primary-hover)';
                      e.currentTarget.style.transform = 'scale(1.1)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'var(--primary-color)';
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                    title="Scroll to bottom"
                  >
                    <i className="bi bi-arrow-down"></i>
                  </button>
                )}
              </div>

              {/* Deep Search Controls */}
              <div style={{
                padding: '0.75rem 1.5rem',
                borderTop: '1px solid var(--border-color)',
                background: 'var(--surface-color)',
                flexShrink: 0
              }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  color: 'var(--text-primary)',
                  userSelect: 'none'
                }}>
                  <input
                    type="checkbox"
                    checked={deepSearchEnabled}
                    onChange={(e) => handleDeepSearchToggle(e.target.checked)}
                    disabled={isLoading}
                    style={{
                      width: '1rem',
                      height: '1rem',
                      accentColor: 'var(--primary-color)'
                    }}
                  />
                  <i className="bi bi-search" style={{ color: 'var(--primary-color)' }}></i>
                  <span>Deep Research</span>
                  <i className="bi bi-info-circle" style={{ color: 'var(--text-muted)', marginLeft: 'auto' }}></i>
                </label>
                {deepSearchEnabled && (
                  <div style={{
                    marginTop: '0.5rem',
                    padding: '0.5rem',
                    background: 'rgba(59, 130, 246, 0.1)',
                    border: '1px solid rgba(59, 130, 246, 0.3)',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '0.75rem',
                    color: 'var(--primary-color)'
                  }}>
                    🌐 Deep search enabled - responses will include internet research
                  </div>
                )}
              </div>

              {/* Chat Input */}
              <div style={{
                padding: '1.5rem',
                borderTop: '1px solid var(--border-color)',
                background: 'var(--bg-color)',
                flexShrink: 0
              }}>
                {/* Quick Prompts */}
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block' }}>Quick Analysis:</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <button
                      style={{
                        padding: '0.375rem 0.75rem',
                        fontSize: '0.875rem',
                        border: '1px solid var(--primary-color)',
                        background: 'var(--bg-color)',
                        color: 'var(--primary-color)',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        transition: 'all var(--transition-normal)'
                      }}
                      onClick={() => applySuggestion('Summarize the key points from these documents')}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--primary-color)';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--bg-color)';
                        e.currentTarget.style.color = 'var(--primary-color)';
                      }}
                    >
                      <i className="bi bi-file-text" style={{ marginRight: '0.25rem' }}></i>Summarize
                    </button>
                    <button
                      style={{
                        padding: '0.375rem 0.75rem',
                        fontSize: '0.875rem',
                        border: '1px solid var(--primary-color)',
                        background: 'var(--bg-color)',
                        color: 'var(--primary-color)',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        transition: 'all var(--transition-normal)'
                      }}
                      onClick={() => applySuggestion('Create a timeline of events mentioned in the documents')}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--primary-color)';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--bg-color)';
                        e.currentTarget.style.color = 'var(--primary-color)';
                      }}
                    >
                      <i className="bi bi-clock-history" style={{ marginRight: '0.25rem' }}></i>Timeline
                    </button>
                    <button
                      style={{
                        padding: '0.375rem 0.75rem',
                        fontSize: '0.875rem',
                        border: '1px solid var(--primary-color)',
                        background: 'var(--bg-color)',
                        color: 'var(--primary-color)',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        transition: 'all var(--transition-normal)'
                      }}
                      onClick={() => applySuggestion('Compare the documents and highlight differences')}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--primary-color)';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--bg-color)';
                        e.currentTarget.style.color = 'var(--primary-color)';
                      }}
                    >
                      <i className="bi bi-arrow-left-right" style={{ marginRight: '0.25rem' }}></i>Compare
                    </button>
                    <button
                      style={{
                        padding: '0.375rem 0.75rem',
                        fontSize: '0.875rem',
                        border: '1px solid var(--primary-color)',
                        background: 'var(--bg-color)',
                        color: 'var(--primary-color)',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        transition: 'all var(--transition-normal)'
                      }}
                      onClick={() => applySuggestion('Review this NDA', 'review')}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--primary-color)';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--bg-color)';
                        e.currentTarget.style.color = 'var(--primary-color)';
                      }}
                    >
                      <i className="bi bi-clipboard-check" style={{ marginRight: '0.25rem' }}></i>Review
                    </button>
                    <button
                      style={{
                        padding: '0.375rem 0.75rem',
                        fontSize: '0.875rem',
                        border: '1px solid var(--primary-color)',
                        background: 'var(--bg-color)',
                        color: 'var(--primary-color)',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        transition: 'all var(--transition-normal)'
                      }}
                      onClick={() => applySuggestion('Find any inconsistencies or issues in the documents')}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--primary-color)';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--bg-color)';
                        e.currentTarget.style.color = 'var(--primary-color)';
                      }}
                    >
                      <i className="bi bi-exclamation-triangle" style={{ marginRight: '0.25rem' }}></i>Find Issues
                    </button>
                    <button
                      style={{
                        padding: '0.375rem 0.75rem',
                        fontSize: '0.875rem',
                        border: '1px solid var(--primary-color)',
                        background: 'var(--bg-color)',
                        color: 'var(--primary-color)',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        transition: 'all var(--transition-normal)'
                      }}
                      onClick={() => applySuggestion('Help me brainstorm on this case', 'brainstorm')}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--primary-color)';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--bg-color)';
                        e.currentTarget.style.color = 'var(--primary-color)';
                      }}
                    >
                      <i className="bi bi-lightbulb" style={{ marginRight: '0.25rem' }}></i>Brainstorm
                    </button>
                    <button
                      style={{
                        padding: '0.375rem 0.75rem',
                        fontSize: '0.875rem',
                        border: '1px solid var(--primary-color)',
                        background: 'var(--bg-color)',
                        color: 'var(--primary-color)',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        transition: 'all var(--transition-normal)'
                      }}
                      onClick={() => applySuggestion('Draft a professional correspondence', 'draft_correspondence')}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--primary-color)';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--bg-color)';
                        e.currentTarget.style.color = 'var(--primary-color)';
                      }}
                    >
                      <i className="bi bi-envelope" style={{ marginRight: '0.25rem' }}></i>Draft Letter
                    </button>
                    <button
                      style={{
                        padding: '0.375rem 0.75rem',
                        fontSize: '0.875rem',
                        border: '1px solid var(--primary-color)',
                        background: 'var(--bg-color)',
                        color: 'var(--primary-color)',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        transition: 'all var(--transition-normal)'
                      }}
                      onClick={() => applySuggestion('Draft a demand letter', 'draft_demand')}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--primary-color)';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--bg-color)';
                        e.currentTarget.style.color = 'var(--primary-color)';
                      }}
                    >
                      <i className="bi bi-exclamation-circle" style={{ marginRight: '0.25rem' }}></i>Demand Letter
                    </button>
                    <button
                      style={{
                        padding: '0.375rem 0.75rem',
                        fontSize: '0.875rem',
                        border: '1px solid var(--primary-color)',
                        background: 'var(--bg-color)',
                        color: 'var(--primary-color)',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        transition: 'all var(--transition-normal)'
                      }}
                      onClick={() => applySuggestion('Create discovery requests', 'discovery_request')}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--primary-color)';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--bg-color)';
                        e.currentTarget.style.color = 'var(--primary-color)';
                      }}
                    >
                      <i className="bi bi-search" style={{ marginRight: '0.25rem' }}></i>Discovery Request
                    </button>
                    <button
                      style={{
                        padding: '0.375rem 0.75rem',
                        fontSize: '0.875rem',
                        border: '1px solid var(--primary-color)',
                        background: 'var(--bg-color)',
                        color: 'var(--primary-color)',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        transition: 'all var(--transition-normal)'
                      }}
                      onClick={() => applySuggestion('Draft discovery responses', 'discovery_response')}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--primary-color)';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--bg-color)';
                        e.currentTarget.style.color = 'var(--primary-color)';
                      }}
                    >
                      <i className="bi bi-reply" style={{ marginRight: '0.25rem' }}></i>Discovery Response
                    </button>
                    <button
                      style={{
                        padding: '0.375rem 0.75rem',
                        fontSize: '0.875rem',
                        border: '1px solid var(--primary-color)',
                        background: 'var(--bg-color)',
                        color: 'var(--primary-color)',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        transition: 'all var(--transition-normal)'
                      }}
                      onClick={() => applySuggestion('Create an outline', 'create_outline')}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--primary-color)';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--bg-color)';
                        e.currentTarget.style.color = 'var(--primary-color)';
                      }}
                    >
                      <i className="bi bi-list-nested" style={{ marginRight: '0.25rem' }}></i>Create Outline
                    </button>
                    <button
                      style={{
                        padding: '0.375rem 0.75rem',
                        fontSize: '0.875rem',
                        border: '1px solid var(--primary-color)',
                        background: 'var(--bg-color)',
                        color: 'var(--primary-color)',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        transition: 'all var(--transition-normal)'
                      }}
                      onClick={() => applySuggestion('Summarize this brief', 'summarize_brief')}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--primary-color)';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--bg-color)';
                        e.currentTarget.style.color = 'var(--primary-color)';
                      }}
                    >
                      <i className="bi bi-file-earmark-text" style={{ marginRight: '0.25rem' }}></i>Summarize Brief
                    </button>
                    <button
                      style={{
                        padding: '0.375rem 0.75rem',
                        fontSize: '0.875rem',
                        border: '1px solid var(--primary-color)',
                        background: 'var(--bg-color)',
                        color: 'var(--primary-color)',
                        borderRadius: 'var(--radius-sm)',
                        cursor: 'pointer',
                        transition: 'all var(--transition-normal)'
                      }}
                      onClick={() => applySuggestion('Query specific information from documents', 'query_document')}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'var(--primary-color)';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'var(--bg-color)';
                        e.currentTarget.style.color = 'var(--primary-color)';
                      }}
                    >
                      <i className="bi bi-question-circle" style={{ marginRight: '0.25rem' }}></i>Query Document
                    </button>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <input
                    type="text"
                    style={{
                      flex: 1,
                      padding: '0.75rem',
                      border: '1px solid var(--border-color)',
                      borderRadius: 'var(--radius-md)',
                      fontSize: '0.9rem',
                      outline: 'none',
                      background: 'var(--surface-color)',
                      color: 'var(--text-primary)'
                    }}
                    placeholder="Ask about your documents..."
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={isLoading}
                    onFocus={(e) => e.currentTarget.style.borderColor = 'var(--primary-color)'}
                    onBlur={(e) => e.currentTarget.style.borderColor = 'var(--border-color)'}
                  />
                  <button
                    style={{
                      padding: '0.75rem 1rem',
                      background: isLoading || !inputMessage.trim() ? 'var(--border-color)' : 'var(--primary-color)',
                      color: isLoading || !inputMessage.trim() ? 'var(--text-muted)' : 'white',
                      border: 'none',
                      borderRadius: 'var(--radius-md)',
                      cursor: isLoading || !inputMessage.trim() ? 'not-allowed' : 'pointer',
                      transition: 'all var(--transition-normal)'
                    }}
                    onClick={handleSendMessage}
                    disabled={isLoading || !inputMessage.trim()}
                    onMouseEnter={(e) => {
                      if (!isLoading && inputMessage.trim()) {
                        e.currentTarget.style.background = 'var(--primary-hover)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isLoading && inputMessage.trim()) {
                        e.currentTarget.style.background = 'var(--primary-color)';
                      }
                    }}
                  >
                    <i className="bi bi-send"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Document Selection Sidebar */}
          <div style={{
            width: '320px',
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{
              background: 'var(--bg-color)',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border-color)',
              boxShadow: 'var(--shadow-sm)',
              display: 'flex',
              flexDirection: 'column',
              height: '100%'
            }}>
              <div style={{
                padding: '1.5rem 1.5rem 1rem 1.5rem',
                borderBottom: '1px solid var(--border-color)',
                flexShrink: 0
              }}>
                <h6 style={{ margin: 0, color: 'var(--text-primary)', fontWeight: 600 }}>Document Selection</h6>
              </div>
              <div style={{
                padding: '1.5rem',
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0
              }}>
                {/* Project Selection */}
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block' }}>Project:</label>
                  <select
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      border: '1px solid var(--border-color)',
                      borderRadius: 'var(--radius-sm)',
                      fontSize: '0.875rem',
                      background: 'var(--surface-color)',
                      color: 'var(--text-primary)'
                    }}
                    value={selectedProject}
                    onChange={(e) => handleProjectChange(e.target.value)}
                    disabled={isProjectsLoading}
                  >
                    {isProjectsLoading ? (
                      <option>Loading projects...</option>
                    ) : (
                      <>
                        <option value="">Choose project...</option>
                        {projects.map(project => (
                          <option key={project.id} value={project.id.toString()}>
                            {project.name}
                          </option>
                        ))}
                      </>
                    )}
                  </select>
                </div>

                {/* Document Selection */}
                <div style={{ marginBottom: '1rem', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', margin: 0 }}>Documents:</label>
                    <button
                      style={{
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.75rem',
                        border: '1px solid var(--primary-color)',
                        background: 'var(--bg-color)',
                        color: 'var(--primary-color)',
                        borderRadius: 'var(--radius-sm)',
                        cursor: projectDocuments.length === 0 ? 'not-allowed' : 'pointer',
                        opacity: projectDocuments.length === 0 ? 0.5 : 1
                      }}
                      onClick={handleToggleAllDocuments}
                      disabled={projectDocuments.length === 0}
                    >
                      {selectedDocuments.length === projectDocuments.length ? 'None' : 'All'}
                    </button>
                  </div>
                  <div style={{
                    flex: 1,
                    overflowY: 'auto',
                    minHeight: '150px',
                    maxHeight: 'none'
                  }}>
                    {isDocumentsLoading ? (
                      <div>
                        <Skeleton count={3} height={24} style={{ marginBottom: '0.5rem' }} />
                      </div>
                    ) : projectDocuments.length === 0 ? (
                      <small style={{ color: 'var(--text-secondary)' }}>
                        {selectedProject ? 'No completed documents in this project' : 'Select a project first'}
                      </small>
                    ) : (
                      projectDocuments.map((doc) => (
                        <div key={doc.id} style={{ display: 'flex', alignItems: 'center', marginBottom: '0.5rem' }}>
                          <input
                            type="checkbox"
                            id={`doc-${doc.id}`}
                            checked={selectedDocuments.includes(doc.id)}
                            onChange={() => handleDocumentToggle(doc.id)}
                            style={{ marginRight: '0.5rem' }}
                          />
                          <label htmlFor={`doc-${doc.id}`} style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', cursor: 'pointer' }}>
                            <i className={`bi bi-file-earmark-${getFileIcon(doc.file_type)}`} style={{ marginRight: '0.5rem' }}></i>
                            {doc.display_name || doc.original_filename}
                          </label>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Suggestions */}
                <div style={{ flexShrink: 0 }}>
                  <label style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)', marginBottom: '0.5rem', display: 'block' }}>Suggestions:</label>
                  <div style={{
                    maxHeight: '120px',
                    overflowY: 'auto'
                  }}>
                    {suggestions.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                        <small style={{ color: 'var(--text-secondary)' }}>No suggestions available</small>
                      </div>
                    ) : (
                      suggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          style={{
                            width: '100%',
                            textAlign: 'left',
                            padding: '0.5rem',
                            border: 'none',
                            background: 'none',
                            color: 'var(--text-primary)',
                            fontSize: '0.875rem',
                            cursor: 'pointer',
                            borderRadius: 'var(--radius-sm)',
                            transition: 'background var(--transition-normal)'
                          }}
                          onClick={() => applySuggestion(suggestion)}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-color)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'none'}
                        >
                          <i className="bi bi-chat-quote" style={{ marginRight: '0.5rem' }}></i>
                          {suggestion}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>


        {/* Deep Search Confirmation Dialog */}
        {showDeepSearchDialog && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}>
            <div style={{
              background: 'var(--bg-color)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              maxWidth: '500px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '1.5rem 1.5rem 1rem',
                borderBottom: '1px solid var(--border-color)'
              }}>
                <i className="bi bi-search" style={{ fontSize: '1.25rem', color: 'var(--primary-color)' }}></i>
                <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                  Enable Deep Research?
                </h3>
              </div>
              <div style={{ padding: '1.5rem' }}>
                <p style={{
                  margin: '0 0 1.5rem',
                  color: 'var(--text-secondary)',
                  lineHeight: '1.6'
                }}>
                  In Deep Research, the AnCore will search the internet and other external resources
                  to provide more thorough and up-to-date responses. This may take longer but will
                  give you more comprehensive answers with current market data and trends.
                </p>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    background: 'var(--surface-color)',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border-color)'
                  }}>
                    <span style={{ fontSize: '1.25rem' }}>🌐</span>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>Real-time web search</span>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    background: 'var(--surface-color)',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border-color)'
                  }}>
                    <span style={{ fontSize: '1.25rem' }}>📊</span>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>Current market data</span>
                  </div>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    background: 'var(--surface-color)',
                    borderRadius: 'var(--radius-sm)',
                    border: '1px solid var(--border-color)'
                  }}>
                    <span style={{ fontSize: '1.25rem' }}>🔍</span>
                    <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>Enhanced research capabilities</span>
                  </div>
                </div>
              </div>
              <div style={{
                display: 'flex',
                gap: '0.75rem',
                justifyContent: 'flex-end',
                padding: '1rem 1.5rem 1.5rem',
                borderTop: '1px solid var(--border-color)'
              }}>
                <button
                  style={{
                    background: 'var(--bg-color)',
                    color: 'var(--text-primary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: 'var(--radius-sm)',
                    padding: '0.75rem 1.5rem',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onClick={handleDeepSearchCancel}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--surface-color)';
                    e.currentTarget.style.borderColor = 'var(--text-muted)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'var(--bg-color)';
                    e.currentTarget.style.borderColor = 'var(--border-color)';
                  }}
                >
                  Cancel
                </button>
                <button
                  style={{
                    background: 'var(--primary-color)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    padding: '0.75rem 1.5rem',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    cursor: 'pointer',
                    transition: 'background 0.2s'
                  }}
                  onClick={handleDeepSearchConfirm}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'var(--primary-hover)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'var(--primary-color)';
                  }}
                >
                  Enable Deep Search
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Source Details Modal */}
        {showSourceModal && selectedSource && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '1rem'
          }}>
            <div style={{
              background: 'var(--bg-color)',
              borderRadius: 'var(--radius-lg)',
              padding: '1.5rem',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '80vh',
              overflowY: 'auto',
              boxShadow: 'var(--shadow-xl)'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '1rem',
                paddingBottom: '1rem',
                borderBottom: '1px solid var(--border-color)'
              }}>
                <h5 style={{ margin: 0, color: 'var(--text-primary)', fontWeight: 600 }}>
                  <i className="bi bi-file-earmark-text" style={{ marginRight: '0.5rem' }}></i>
                  Source Details
                </h5>
                <button
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '1.5rem',
                    cursor: 'pointer',
                    color: 'var(--text-secondary)',
                    padding: '0.25rem'
                  }}
                  onClick={() => setShowSourceModal(false)}
                >
                  <i className="bi bi-x"></i>
                </button>
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <div style={{
                  background: 'var(--surface-color)',
                  padding: '1rem',
                  borderRadius: 'var(--radius-md)',
                  border: '1px solid var(--border-color)'
                }}>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <strong style={{ color: 'var(--text-primary)', fontSize: '0.875rem' }}>Document:</strong>
                    <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                      <i className="bi bi-file-earmark-text" style={{ marginRight: '0.5rem' }}></i>
                      {(() => {
                        const docId = selectedSource.metadata?.document_id;
                        const document = projectDocuments.find(doc => doc.id === docId);
                        return document ? (document.display_name || document.original_filename) : (selectedSource.metadata?.filename || `Document ID ${docId}`);
                      })()}
                    </div>
                  </div>

                  {selectedSource.metadata?.page && (
                    <div style={{ marginBottom: '0.75rem' }}>
                      <strong style={{ color: 'var(--text-primary)', fontSize: '0.875rem' }}>Page:</strong>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                        <i className="bi bi-file-earmark" style={{ marginRight: '0.5rem' }}></i>
                        Page {selectedSource.metadata.page}
                      </div>
                    </div>
                  )}

                  {selectedSource.metadata?.section && (
                    <div>
                      <strong style={{ color: 'var(--text-primary)', fontSize: '0.875rem' }}>Section:</strong>
                      <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                        <i className="bi bi-bookmark" style={{ marginRight: '0.5rem' }}></i>
                        {selectedSource.metadata.section}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <strong style={{ color: 'var(--text-primary)', fontSize: '0.875rem', marginBottom: '0.5rem', display: 'block' }}>
                  Content:
                </strong>
                <div
                  style={{
                    background: 'var(--surface-color)',
                    padding: '1rem',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--border-color)',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    fontSize: '0.875rem',
                    lineHeight: '1.5',
                    color: 'var(--text-primary)'
                  }}
                  dangerouslySetInnerHTML={{
                    __html: formatSourceContent(selectedSource.text || '')
                  }}
                />
              </div>

              <div style={{
                marginTop: '1.5rem',
                paddingTop: '1rem',
                borderTop: '1px solid var(--border-color)',
                display: 'flex',
                justifyContent: 'flex-end'
              }}>
                <button
                  style={{
                    padding: '0.5rem 1rem',
                    background: 'var(--primary-color)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    fontSize: '0.875rem',
                    fontWeight: 500
                  }}
                  onClick={() => setShowSourceModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Chat;
